import type { AxiosProgressEvent } from 'axios';
import api from '@/shared/api';
import { buildFormData, buildTruthySearchParams, requestExcelBlobExport } from '@/shared/lib';
import type { ExcelBlobExportResult, SortOrder } from '@/shared/lib';
import type { BulkDeleteResult } from '@/shared/lib/use';
import type {
  CreateOnboardingTrainingPayload,
  CreateOnboardingTrainingResponse,
  CreateOpenTrainingPayload,
  CreateOpenTrainingResponse,
  CreateRoleChangeTrainingPayload,
  CreateRoleChangeTrainingResponse,
  CreateSgqPunctualTrainingPayload,
  CreateSgqPunctualTrainingResponse,
  PaginatedTrainingListResponse,
  TrainingDetail,
  TrainingDetailResponse,
  TrainingListInstructorFilter,
  TrainingStatus,
  TrainingType,
  UpdateHomologationTrainingPayload,
  UpdateHomologationTrainingResponse,
  UpdateOnboardingTrainingPayload,
  UpdateOnboardingTrainingResponse,
  UpdateOpenTrainingPayload,
  UpdateOpenTrainingResponse,
  UpdateRoleChangeTrainingPayload,
  UpdateRoleChangeTrainingResponse,
  UpdateSgqPunctualTrainingPayload,
  UpdateSgqPunctualTrainingResponse,
  UpdateTrainingNotesPayload,
  UpdateTrainingNotesResponse,
  UploadInstructorCurriculumResponse,
} from '../model/types';

const EXPORT_FALLBACK_FILENAME = 'treinamento.xlsx';

const EXPORT_GENERIC_RETRY_MESSAGE = 'Tente novamente.';

export type TrainingListSort = 'createdAt' | 'title';
export type TrainingListOrder = SortOrder;

export interface ListTrainingsParams {
  page?: number;
  limit?: number;
  term?: string;
  sort?: TrainingListSort;
  order?: TrainingListOrder;
  type?: TrainingType;
  instructor?: TrainingListInstructorFilter;
  certificateRequired?: boolean;
  status?: TrainingStatus;
  memberId?: number;
  positionId?: number;
  trainingDate?: string;
}

export type ExportTrainingListParams = Omit<ListTrainingsParams, 'page' | 'limit'>;

/**
 * Busca, filtros e ordenação: a paginação nunca entra na exportação, então o seletor de
 * itens por página não tem como interferir no arquivo gerado.
 */
const TRAINING_LIST_EXPORT_QUERY_KEYS = [
  'term',
  'sort',
  'order',
  'type',
  'instructor',
  'certificateRequired',
  'status',
  'memberId',
  'positionId',
  'trainingDate',
] as const satisfies readonly (keyof ExportTrainingListParams)[];

const LIST_EXPORT_FALLBACK_FILENAME = 'lista-treinamentos.xlsx';

const LIST_EXPORT_RETRY_MESSAGE = 'Tente novamente.';

export interface UploadInstructorCurriculumOptions {
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

class TrainingApi {
  async listTrainings(params: ListTrainingsParams = {}): Promise<PaginatedTrainingListResponse> {
    const response = await api.get<PaginatedTrainingListResponse>('/trainings', { params });
    return response.data;
  }

  /**
   * Baixa a Lista de Treinamentos em Excel. O backend decide o nome do arquivo e a
   * quantidade exportada, então o chamador só repassa o que voltou.
   */
  async exportTrainingListExcel(
    params: ExportTrainingListParams = {}
  ): Promise<ExcelBlobExportResult> {
    const queryParams = buildTruthySearchParams(params, TRAINING_LIST_EXPORT_QUERY_KEYS);

    return requestExcelBlobExport(
      () =>
        api.get(`/trainings/export/excel?${queryParams.toString()}`, {
          responseType: 'blob',
        }),
      LIST_EXPORT_FALLBACK_FILENAME,
      LIST_EXPORT_RETRY_MESSAGE
    );
  }

  async deleteTraining(id: number): Promise<void> {
    await api.delete(`/trainings/${id}`);
  }

  async getControlPdf(id: number): Promise<Blob> {
    const response = await api.get<Blob>(`/trainings/${id}/control/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async bulkDeleteTrainings(ids: number[]): Promise<{ data: BulkDeleteResult }> {
    const response = await api.delete<{ data: BulkDeleteResult }>('/trainings/bulk', {
      data: { ids },
    });
    return response.data;
  }

  async getTrainingById(id: number): Promise<TrainingDetail> {
    const response = await api.get<TrainingDetailResponse>(`/trainings/${id}`);
    return response.data.data;
  }

  /**
   * Completes the training and returns it in the detail shape, so the screen can
   * refresh from the response instead of fetching the detail again.
   */
  async completeTraining(id: number): Promise<TrainingDetail> {
    const response = await api.post<TrainingDetailResponse>(`/trainings/${id}/complete`);
    return response.data.data;
  }

  /**
   * Downloads the training spreadsheet. The backend decides the file name and the
   * layout per training type, so the caller only forwards what came back.
   */
  async exportTrainingExcel(id: number): Promise<ExcelBlobExportResult> {
    return requestExcelBlobExport(
      () => api.get(`/trainings/${id}/export/excel`, { responseType: 'blob' }),
      EXPORT_FALLBACK_FILENAME,
      EXPORT_GENERIC_RETRY_MESSAGE
    );
  }

  async createOnboardingTraining(
    payload: CreateOnboardingTrainingPayload
  ): Promise<CreateOnboardingTrainingResponse> {
    const response = await api.post<CreateOnboardingTrainingResponse>('/trainings/onboarding', payload);
    return response.data;
  }

  async createRoleChangeTraining(
    payload: CreateRoleChangeTrainingPayload
  ): Promise<CreateRoleChangeTrainingResponse> {
    const response = await api.post<CreateRoleChangeTrainingResponse>('/trainings/role-change', payload);
    return response.data;
  }

  async createSgqPunctualTraining(
    payload: CreateSgqPunctualTrainingPayload
  ): Promise<CreateSgqPunctualTrainingResponse> {
    const response = await api.post<CreateSgqPunctualTrainingResponse>('/trainings/sgq-punctual', payload);
    return response.data;
  }

  async createOpenTraining(payload: CreateOpenTrainingPayload): Promise<CreateOpenTrainingResponse> {
    const response = await api.post<CreateOpenTrainingResponse>('/trainings/open', payload);
    return response.data;
  }

  async updateOnboardingTraining(
    id: number,
    payload: UpdateOnboardingTrainingPayload
  ): Promise<UpdateOnboardingTrainingResponse> {
    const response = await api.put<UpdateOnboardingTrainingResponse>(
      `/trainings/onboarding/${id}`,
      payload
    );
    return response.data;
  }

  async updateRoleChangeTraining(
    id: number,
    payload: UpdateRoleChangeTrainingPayload
  ): Promise<UpdateRoleChangeTrainingResponse> {
    const response = await api.put<UpdateRoleChangeTrainingResponse>(
      `/trainings/role-change/${id}`,
      payload
    );
    return response.data;
  }

  async updateSgqPunctualTraining(
    id: number,
    payload: UpdateSgqPunctualTrainingPayload
  ): Promise<UpdateSgqPunctualTrainingResponse> {
    const response = await api.put<UpdateSgqPunctualTrainingResponse>(
      `/trainings/sgq-punctual/${id}`,
      payload
    );
    return response.data;
  }

  async updateOpenTraining(
    id: number,
    payload: UpdateOpenTrainingPayload
  ): Promise<UpdateOpenTrainingResponse> {
    const response = await api.put<UpdateOpenTrainingResponse>(`/trainings/open/${id}`, payload);
    return response.data;
  }

  async updateHomologationTraining(
    id: number,
    payload: UpdateHomologationTrainingPayload
  ): Promise<UpdateHomologationTrainingResponse> {
    const response = await api.put<UpdateHomologationTrainingResponse>(
      `/trainings/homologation/${id}`,
      payload
    );
    return response.data;
  }

  async updateTrainingNotes(
    id: number,
    payload: UpdateTrainingNotesPayload
  ): Promise<UpdateTrainingNotesResponse> {
    const response = await api.put<UpdateTrainingNotesResponse>(`/trainings/${id}`, payload);
    return response.data;
  }

  async uploadInstructorCurriculum(
    file: File,
    options: UploadInstructorCurriculumOptions = {}
  ): Promise<UploadInstructorCurriculumResponse> {
    const response = await api.post<UploadInstructorCurriculumResponse>(
      '/trainings/open/curriculum',
      buildFormData({ file }),
      {
        headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
        onUploadProgress: options.onUploadProgress,
        signal: options.signal,
      }
    );
    return response.data;
  }

  async getInstructorCurriculumFile(sessionId: number): Promise<Blob> {
    const response = await api.get<Blob>(`/trainings/open/curriculum/${sessionId}`, {
      responseType: 'blob',
    });
    return response.data;
  }
}

export const trainingApi = new TrainingApi();
